import { Code } from '@connectrpc/connect';
import { type Event, type Extras, type Primitive } from '@sentry/core';
import { type DefaultError } from '@tanstack/react-query';
import { type AnyRouter } from '@tanstack/react-router';
import { merge } from 'ts-deepmerge';
import { getCurrentRouteDomain, getCurrentRouteId } from '@/router/utils';
import { isObject } from '@/utils/utils';
import { isAccessControlError, isFetchError, isPublicApiError, type SentryContext } from './errors';

/**
 * Where in the data layer the error was caught.
 */
export type CaptureSurface = 'query' | 'mutation';

/**
 * Domains are derived from the proto service type name, e.g. 'qdrant.cloud.billing.v1.BillingService' → 'billing'.
 * This map renames package segments that don't read well as dashboard values.
 */
const DOMAIN_ALIASES: Record<string, string> = {
  hybrid: 'hybrid-cloud',
};

type ConnectKeyParams = { serviceName: string; methodName?: string };

/**
 * Extracts the service/method identifiers from a Connect query/mutation key.
 * @see https://connectrpc.com/docs/web/query/#query-keys for the key shape
 */
function getConnectKeyParams(queryKey: readonly unknown[] | undefined): ConnectKeyParams | undefined {
  if (!Array.isArray(queryKey) || queryKey[0] !== 'connect-query') {
    return undefined;
  }
  const params = queryKey[1];
  if (isObject(params) && 'serviceName' in params && typeof params.serviceName === 'string') {
    return {
      serviceName: params.serviceName,
      methodName: 'methodName' in params && typeof params.methodName === 'string' ? params.methodName : undefined,
    };
  }
  return undefined;
}

function getDomain(serviceName: string): string | undefined {
  const segment = serviceName.startsWith('qdrant.cloud.') ? serviceName.split('.').at(2) : undefined;
  if (!segment) {
    return undefined;
  }
  return DOMAIN_ALIASES[segment] ?? segment;
}

/**
 * Derives the canonical Sentry tags for a query/mutation error from data already available at the
 * QueryClient choke point, so that every captured API error is filterable in Sentry without any
 * call-site instrumentation:
 *
 * - `error.kind` — error class name
 * - `surface` — 'query' | 'mutation'
 * - `route.id` — route matched when the error occurred
 * - `rpc.service` / `rpc.method` / `domain` — from the Connect query key
 * - `grpc.code` / `qc.error_code` / `qc.trace_id` — from PublicApiError metadata
 * - `http.status` — from FetchError
 *
 * Explicit `meta.sentryContext` values take precedence, see {@link mergeSentryContexts}.
 */
export function deriveSentryContext(
  error: DefaultError,
  surface: CaptureSurface,
  queryKey: readonly unknown[] | undefined,
  router: AnyRouter,
): SentryContext {
  const tags: Record<string, Primitive> = {
    'error.kind': error instanceof Error ? error.name : typeof error,
    surface,
  };
  const extra: Extras = {};

  const routeId = getCurrentRouteId(router);
  if (routeId) {
    tags['route.id'] = routeId;
  }

  const keyParams = getConnectKeyParams(queryKey);
  if (keyParams) {
    tags['rpc.service'] = keyParams.serviceName.split('.').at(-1) ?? keyParams.serviceName;
    if (keyParams.methodName) {
      tags['rpc.method'] = keyParams.methodName;
    }
    const domain = getDomain(keyParams.serviceName);
    if (domain) {
      tags.domain = domain;
    }
  }

  if (isPublicApiError(error)) {
    tags['grpc.code'] = Code[error.code];
    if (error.qdrantError?.code) {
      tags['qc.error_code'] = error.qdrantError.code;
    }
    if (error.traceId) {
      tags['qc.trace_id'] = error.traceId;
    }
  } else if (isFetchError(error)) {
    tags['http.status'] = error.status;
  } else if (isAccessControlError(error)) {
    extra.missing_permissions = error.permissions;
    extra.missing_privileges = error.privileges;
  }

  // Proto package segments are the source of truth for the derived `domain`, so the value is a plain
  // string here; the typed {@link SentryTags} union only polices manually-authored reports.
  return { tags, extra };
}

/**
 * Fills route-derived tags onto an event that didn't pass through the QueryClient choke point — where
 * {@link deriveSentryContext} captures them at error time — so runtime errors, third-party throws and
 * direct-to-cluster fetches stay filterable by `route.id` and owner `domain`. Both are gap-fills: an
 * explicit or API-derived value already on the event (captured at error time) always wins, so the
 * route-derived value never degrades the more precise one.
 *
 * `route.id` is read from the leaf match; `domain` from the nearest annotated ancestor, see
 * {@link getCurrentRouteId} and {@link getCurrentRouteDomain}.
 */
export function deriveRouteFallbackTags(tags: Event['tags'], router: AnyRouter): Event['tags'] {
  let result = tags;
  if (!result?.['route.id']) {
    const routeId = getCurrentRouteId(router);
    if (routeId) {
      result = { ...result, 'route.id': routeId };
    }
  }
  if (!result?.domain) {
    const domain = getCurrentRouteDomain(router);
    if (domain) {
      result = { ...result, domain };
    }
  }
  return result;
}

/**
 * Fills the backend correlation tags onto an event whose error never passed through the QueryClient
 * choke point — where {@link deriveSentryContext} reads them at error time. A `PublicApiError` can
 * reach Sentry from a `reportError()` call site, an error boundary or a route resolver instead, and
 * those captures would otherwise land without the `qc-trace-id` the backend returned, leaving no way
 * to follow the failure into the service that produced it.
 *
 * Gap-fill only: a value already on the event (captured at error time) always wins.
 */
export function deriveApiFallbackTags(tags: Event['tags'], error: unknown): Event['tags'] {
  if (!isPublicApiError(error)) {
    return tags;
  }
  let result = tags;
  if (!result?.['grpc.code']) {
    result = { ...result, 'grpc.code': Code[error.code] };
  }
  if (!result['qc.trace_id'] && error.traceId) {
    result = { ...result, 'qc.trace_id': error.traceId };
  }
  return result;
}

/**
 * Path segments that look like resource identifiers (numeric, hex hashes, uuids).
 */
const ID_LIKE_SEGMENT = /^(\d+|[\da-f]{8,}|[\da-f][\da-f-]{14,})$/i;

/**
 * Reduces a request URL to a path template by collapsing id-like segments,
 * so fingerprints don't split into one issue per resource.
 */
function normalizePathname(url: string): string {
  let pathname = url;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    // Keep the raw value if it isn't a parsable URL.
  }
  return pathname
    .split('/')
    .map((segment) => (ID_LIKE_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
}

function asString(value: Primitive): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Computes an explicit Sentry issue fingerprint for API errors, so issues group by what failed
 * (endpoint × failure class) instead of by stack trace — which would split the same failing call
 * across call sites — or by server messages, which may contain dynamic values.
 */
export function getFetchRelatedErrorFingerprint(error: unknown, tags: Event['tags']): string[] | undefined {
  if (isPublicApiError(error)) {
    const service = asString(tags?.['rpc.service']);
    if (!service) {
      // Unknown RPC source (e.g. a manual capture): keep default stack-based grouping, refined by code.
      return ['{{ default }}', Code[error.code]];
    }
    const method = asString(tags?.['rpc.method']) ?? '';
    return ['public-api-error', service, method, Code[error.code], error.qdrantError?.code ?? ''];
  }
  if (isFetchError(error)) {
    return ['fetch-error', normalizePathname(error.url), String(error.status)];
  }
  return undefined;
}

/**
 * Merges a derived (automatic) Sentry context with an explicit one from query/mutation `meta`.
 * Explicit values win on scalar leaf conflicts (level, ignore, and individual tags/extras), nested
 * objects are deep-merged, and arrays from both sides are concatenated (ts-deepmerge default).
 */
export function mergeSentryContexts(derived: SentryContext, explicit: SentryContext | undefined): SentryContext {
  if (!explicit) {
    return derived;
  }
  return merge(derived, explicit);
}
